import { WrapperWidget, ProgressBar } from '@agentselly/ui';
import { AppLink } from '~/components/AppLink';
import { useTranslate } from '@tolgee/react';
import { Button } from '~/components/ui/Button';
import { ArrowRight } from '~/generated/svg/ArrowRight';

interface Props {
  readonly link: string;
  readonly linkList: {
    readonly text: string;
    readonly link: string;
  }[];
  readonly rating: number;
}

export function EvaluationScoreWidget(props: Props) {
  const { t } = useTranslate();
  function getLevelTextKey(rating: number) {
    if (rating <= 2) return 'evaluation_score_widget_text_low'; // 1-2
    if (rating < 5) return 'evaluation_score_widget_text_medium'; // 3-4
    if (rating === 5) return 'evaluation_score_widget_text_high'; // 5
  }

  const levelTextKey = getLevelTextKey(props.rating);

  return (
    <WrapperWidget>
      <div className="h-full flex flex-col gap-y-6">
        <h2 className="text-lg font-bold">
          {t('evaluation_score_widget_title')}
        </h2>
        <p>{t(levelTextKey + '_1')}</p>
        <Rating rating={props.rating || 1} />

        {props.rating !== 5 && (
          <>
            <p>{t(levelTextKey + '_2')}</p>
            {props.linkList.map((link, idx) => (
              <AppLink
                key={idx}
                to={link?.link}
                className="flex items-center underline text-blue-500 hover:text-blue-700 gap-4"
              >
                <ArrowRight
                  width={15}
                  height={15}
                />
                {t(link?.text)}
              </AppLink>
            ))}
          </>
        )}

        <p>{t('evaluation_score_widget_text_3')}</p>
        <AppLink
          to={props.link}
          className="self-start"
        >
          <Button
            primary
            showArrow
          >
            {t('evaluation_score_widget_primary_button_text')}
          </Button>
        </AppLink>
      </div>
    </WrapperWidget>
  );
}

function Rating(props: { readonly rating: number }) {
  const MAX_RATING = 5;
  const { t } = useTranslate();
  const ratingTotal = MAX_RATING;
  const ratingPercentage = (props.rating / ratingTotal) * 100;
  const ratingText = `${props.rating}/${MAX_RATING}`;
  const pointsLeft = ratingTotal - props.rating;

  return (
    <div className="w-full">
      <div className="space-y-4">
        <ProgressBar
          value={ratingPercentage}
          max={100}
          size="lg"
          variant="primary"
          showLabel={true}
          label={t('evaluation_score_widget_progress_label', {
            rating: ratingText,
          })}
          className="w-full"
        />

        <div className="flex justify-between text-sm">
          <span className="font-bold">{ratingText}</span>
          {Boolean(pointsLeft) && (
            <span className="font-bold text-muted-foreground">
              {t('evaluation_score_widget_progress_text', {
                pointsLeft: pointsLeft,
              })}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
